import { defineStore } from "pinia";
import { computed, reactive, ref, watch } from "vue";

import {
  addWishlistCompany,
  getCompanyWishlist,
  getInbox,
  MICROSOFT_OAUTH_START_PATH,
  removeWishlistCompany,
  syncInbox,
  trackWishlistCompany,
} from "@/api/inboxWishlist";
import {
  COMPANY_WISHLIST_CACHE_KEY,
  invalidateCompanyWishlistCache,
} from "@/cache/companyWishlistCache";
import { resourceCache } from "@/cache/resourceCache";
import type {
  CompanyWishlistResponse,
  InboxMessage,
  InboxResponse,
  InboxWishlistPhase,
  MailboxAccount,
  SponsorCompanyOption,
  WishlistSummary,
  WishlistCompany,
} from "@/types/inboxWishlist";
import { useUserStore } from "@/stores/user";
import {
  DEFAULT_WISHLIST_FILTERS,
  filterWishlistCompanies,
  paginateWishlistCompanies,
  WISHLIST_PAGE_SIZE,
  wishlistIndustries,
  type WishlistFilters,
} from "@/utils/companyWishlistPresentation";

const WISHLIST_TTL_MS = 30_000;
const WISHLIST_STALE_TTL_MS = 5 * 60_000;

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function isAbortError(caught: unknown): boolean {
  return caught instanceof DOMException && caught.name === "AbortError";
}

export const useInboxStore = defineStore("inbox", () => {
  const phase = ref<InboxWishlistPhase>("loading");
  const accounts = ref<MailboxAccount[]>([]);
  const messages = ref<InboxMessage[]>([]);
  const errorMessage = ref("");
  const notice = ref("");
  let activeController: AbortController | null = null;
  let generation = 0;

  const isUpdating = computed(() => phase.value === "updating");
  const hasMailboxActivity = computed(
    () => accounts.value.length > 0 || messages.value.length > 0,
  );
  const syncDisabledReason = computed(() => {
    if (phase.value === "error")
      return "Retry loading the inbox before syncing.";
    if (phase.value === "loading")
      return "Wait for the inbox to finish loading.";
    return "";
  });

  function owns(
    controller: AbortController,
    requestGeneration: number,
  ): boolean {
    return (
      activeController === controller &&
      generation === requestGeneration &&
      !controller.signal.aborted
    );
  }

  function beginRequest(): {
    controller: AbortController;
    requestGeneration: number;
  } {
    activeController?.abort();
    const controller = new AbortController();
    activeController = controller;
    return { controller, requestGeneration: ++generation };
  }

  function applyInbox(payload: InboxResponse): void {
    accounts.value = payload.accounts;
    messages.value = payload.inbox;
    phase.value =
      accounts.value.length || messages.value.length ? "success" : "empty";
  }

  async function load(): Promise<void> {
    const { controller, requestGeneration } = beginRequest();
    phase.value = hasMailboxActivity.value ? "updating" : "loading";
    errorMessage.value = "";
    notice.value = "";
    try {
      const payload = await getInbox(controller.signal);
      if (owns(controller, requestGeneration)) applyInbox(payload);
    } catch (caught: unknown) {
      if (!owns(controller, requestGeneration) || isAbortError(caught)) return;
      phase.value = hasMailboxActivity.value ? "success" : "error";
      errorMessage.value = errorText(caught, "The inbox could not be loaded.");
    } finally {
      if (activeController === controller) activeController = null;
    }
  }

  async function syncMailbox(): Promise<void> {
    if (
      phase.value === "loading" ||
      phase.value === "error" ||
      isUpdating.value
    )
      return;
    const { controller, requestGeneration } = beginRequest();
    phase.value = "updating";
    errorMessage.value = "";
    notice.value = "";
    try {
      const payload = await syncInbox(controller.signal);
      if (!owns(controller, requestGeneration)) return;
      applyInbox(payload);
      const imported = payload.sync.imported;
      notice.value = `Imported ${imported} message${imported === 1 ? "" : "s"}`;
    } catch (caught: unknown) {
      if (!owns(controller, requestGeneration) || isAbortError(caught)) return;
      phase.value = hasMailboxActivity.value ? "success" : "empty";
      errorMessage.value = errorText(caught, "Mailbox sync failed.");
    } finally {
      if (activeController === controller) activeController = null;
    }
  }

  function connectOutlook(): void {
    window.location.assign(MICROSOFT_OAUTH_START_PATH);
  }

  function dispose(): void {
    generation += 1;
    activeController?.abort();
    activeController = null;
    if (phase.value === "updating") {
      phase.value = hasMailboxActivity.value ? "success" : "empty";
    }
  }

  function reset(): void {
    dispose();
    phase.value = "loading";
    accounts.value = [];
    messages.value = [];
    errorMessage.value = "";
    notice.value = "";
  }

  return {
    accounts,
    connectOutlook,
    dispose,
    errorMessage,
    hasMailboxActivity,
    isUpdating,
    load,
    messages,
    notice,
    phase,
    reset,
    syncDisabledReason,
    syncMailbox,
  };
});

export const useWishlistStore = defineStore("wishlist", () => {
  const userStore = useUserStore();
  const phase = ref<InboxWishlistPhase>("loading");
  const wishlist = ref<WishlistCompany[]>([]);
  const sponsorCompanies = ref<SponsorCompanyOption[]>([]);
  const summary = ref<WishlistSummary>(emptySummary());
  const generatedAt = ref("");
  const policyVersion = ref("");
  const companyInput = ref("");
  const activeAction = ref("");
  const errorMessage = ref("");
  const validationMessage = ref("");
  const actionNotice = ref("");
  const isRefreshing = ref(false);
  const pendingActions = reactive<Record<string, "tracking" | "removing">>({});
  const filters = reactive<WishlistFilters>({ ...DEFAULT_WISHLIST_FILTERS });
  const currentPage = ref(1);
  const isAdding = ref(false);
  let activeLoadController: AbortController | null = null;
  let addController: AbortController | null = null;
  const actionControllers = new Map<string, AbortController>();
  let loadGeneration = 0;
  let sessionGeneration = 0;

  const filteredCompanies = computed(() =>
    filterWishlistCompanies(wishlist.value, filters),
  );
  const pageCount = computed(() =>
    Math.max(1, Math.ceil(filteredCompanies.value.length / WISHLIST_PAGE_SIZE)),
  );
  const visibleCompanies = computed(() =>
    paginateWishlistCompanies(filteredCompanies.value, currentPage.value),
  );
  const industries = computed(() => wishlistIndustries(wishlist.value));
  const isUpdating = computed(
    () => isAdding.value || Object.keys(pendingActions).length > 0,
  );
  const companyCount = computed(() => {
    const count = summary.value.saved_companies;
    return `${count} compan${count === 1 ? "y" : "ies"}`;
  });
  const formDisabledReason = computed(() => {
    if (phase.value === "loading")
      return "Wait for the wishlist to finish loading.";
    if (phase.value === "error")
      return "Retry loading the wishlist before making changes.";
    return "";
  });

  function cacheScope(): string | null {
    return userStore.user?.id ?? null;
  }

  function emptySummary(): WishlistSummary {
    return {
      saved_companies: 0,
      ready_to_add: 0,
      monitoring: 0,
      unavailable: 0,
      already_tracking: 0,
    };
  }

  function setPage(value: number): void {
    const page = Number.isFinite(value) ? Math.trunc(value) : 1;
    currentPage.value = Math.min(Math.max(1, page), pageCount.value);
  }

  function clampPage(): void {
    setPage(currentPage.value);
  }

  function applyWishlist(payload: CompanyWishlistResponse): void {
    generatedAt.value = payload.generated_at;
    policyVersion.value = payload.policy_version;
    summary.value = payload.summary;
    wishlist.value = payload.wishlist;
    sponsorCompanies.value = payload.sponsor_companies;
    phase.value = wishlist.value.length > 0 ? "success" : "empty";
    clampPage();
  }

  function ownsLoad(
    controller: AbortController,
    requestGeneration: number,
    requestSession: number,
    scope: string | null,
  ): boolean {
    return (
      activeLoadController === controller &&
      belongsToLoad(controller, requestGeneration, requestSession, scope)
    );
  }

  function belongsToLoad(
    controller: AbortController,
    requestGeneration: number,
    requestSession: number,
    scope: string | null,
  ): boolean {
    return (
      loadGeneration === requestGeneration &&
      sessionGeneration === requestSession &&
      cacheScope() === scope &&
      !controller.signal.aborted
    );
  }

  function beginLoad(scope: string | null): {
    controller: AbortController;
    requestGeneration: number;
    requestSession: number;
  } {
    if (activeLoadController) {
      activeLoadController.abort();
      invalidateCompanyWishlistCache(scope);
    }
    const controller = new AbortController();
    activeLoadController = controller;
    return {
      controller,
      requestGeneration: ++loadGeneration,
      requestSession: sessionGeneration,
    };
  }

  async function seedCache(
    payload: CompanyWishlistResponse,
    scope: string | null,
  ): Promise<void> {
    if (!scope) return;
    invalidateCompanyWishlistCache(scope);
    await resourceCache.read({
      key: COMPANY_WISHLIST_CACHE_KEY,
      loader: async () => payload,
      scope,
      staleTtlMs: WISHLIST_STALE_TTL_MS,
      ttlMs: WISHLIST_TTL_MS,
    });
  }

  async function load(showLoading = true): Promise<void> {
    const scope = cacheScope();
    const { controller, requestGeneration, requestSession } = beginLoad(scope);
    const firstLoad = wishlist.value.length === 0;
    if (showLoading && firstLoad) phase.value = "loading";
    else isRefreshing.value = true;
    errorMessage.value = "";
    try {
      const payload = !showLoading
        ? await getCompanyWishlist(controller.signal)
        : scope
          ? await resourceCache.read({
              key: COMPANY_WISHLIST_CACHE_KEY,
              loader: getCompanyWishlist,
              onRevalidated: (fresh) => {
                if (
                  belongsToLoad(
                    controller,
                    requestGeneration,
                    requestSession,
                    scope,
                  )
                ) {
                  applyWishlist(fresh);
                }
              },
              scope,
              signal: controller.signal,
              staleTtlMs: WISHLIST_STALE_TTL_MS,
              ttlMs: WISHLIST_TTL_MS,
            })
          : await getCompanyWishlist(controller.signal);
      if (!ownsLoad(controller, requestGeneration, requestSession, scope))
        return;
      if (!showLoading) await seedCache(payload, scope);
      if (ownsLoad(controller, requestGeneration, requestSession, scope))
        applyWishlist(payload);
    } catch (caught: unknown) {
      if (
        !ownsLoad(controller, requestGeneration, requestSession, scope) ||
        isAbortError(caught)
      )
        return;
      phase.value = wishlist.value.length ? "success" : "error";
      errorMessage.value = errorText(
        caught,
        "The company wishlist could not be loaded.",
      );
    } finally {
      if (activeLoadController === controller) {
        activeLoadController = null;
        isRefreshing.value = false;
      }
    }
  }

  function validateCompany(): string | null {
    const company = companyInput.value.trim();
    validationMessage.value = company
      ? ""
      : "Enter a company name before adding it.";
    return company || null;
  }

  async function addCompany(company: string): Promise<void> {
    if (formDisabledReason.value || isAdding.value) return;
    cancelLoad(true);
    addController?.abort();
    const controller = new AbortController();
    addController = controller;
    const requestSession = sessionGeneration;
    const scope = cacheScope();
    isAdding.value = true;
    activeAction.value = "add";
    errorMessage.value = "";
    actionNotice.value = "";
    try {
      const payload = await addWishlistCompany(company, controller.signal);
      if (!ownsAdd(controller, requestSession, scope)) return;
      cancelLoad(true);
      await seedCache(payload.overview, scope);
      if (!ownsAdd(controller, requestSession, scope)) return;
      applyWishlist(payload.overview);
      companyInput.value = "";
      actionNotice.value =
        payload.outcome === "created"
          ? "Company saved to your wishlist."
          : "Company is already saved to your wishlist.";
    } catch (caught: unknown) {
      if (!ownsAdd(controller, requestSession, scope) || isAbortError(caught))
        return;
      phase.value = wishlist.value.length ? "success" : "empty";
      errorMessage.value = errorText(caught, "The company could not be added.");
    } finally {
      if (addController === controller) {
        addController = null;
        isAdding.value = false;
        if (activeAction.value === "add")
          activeAction.value = nextActiveAction();
      }
    }
  }

  async function removeCompany(wishlistId: string): Promise<void> {
    const action = beginRowAction(wishlistId, "removing");
    if (!action) return;
    const { controller, requestSession, scope } = action;
    errorMessage.value = "";
    actionNotice.value = "";
    try {
      const payload = await removeWishlistCompany(
        wishlistId,
        controller.signal,
      );
      if (!ownsRowAction(wishlistId, controller, requestSession, scope)) return;
      cancelLoad(true);
      await seedCache(payload.overview, scope);
      if (!ownsRowAction(wishlistId, controller, requestSession, scope)) return;
      applyWishlist(payload.overview);
      actionNotice.value = "Company removed from the wishlist.";
    } catch (caught: unknown) {
      if (
        !ownsRowAction(wishlistId, controller, requestSession, scope) ||
        isAbortError(caught)
      )
        return;
      phase.value = wishlist.value.length ? "success" : "empty";
      errorMessage.value = errorText(
        caught,
        "The company could not be removed.",
      );
    } finally {
      finishRowAction(wishlistId, controller);
    }
  }

  async function trackCompany(wishlistId: string): Promise<void> {
    const action = beginRowAction(wishlistId, "tracking");
    if (!action) return;
    const { controller, requestSession, scope } = action;
    errorMessage.value = "";
    actionNotice.value = "";
    try {
      const payload = await trackWishlistCompany(wishlistId, controller.signal);
      if (!ownsRowAction(wishlistId, controller, requestSession, scope)) return;
      cancelLoad(true);
      await seedCache(payload.overview, scope);
      if (!ownsRowAction(wishlistId, controller, requestSession, scope)) return;
      applyWishlist(payload.overview);
      if (payload.outcome === "created") {
        actionNotice.value = `${payload.created_count} role${payload.created_count === 1 ? "" : "s"} added to tracking.`;
      } else if (payload.outcome === "already_tracking") {
        actionNotice.value =
          "This company's suitable roles are already tracking.";
      } else {
        actionNotice.value = "No suitable roles are ready to add yet.";
      }
    } catch (caught: unknown) {
      if (
        !ownsRowAction(wishlistId, controller, requestSession, scope) ||
        isAbortError(caught)
      )
        return;
      phase.value = wishlist.value.length ? "success" : "empty";
      errorMessage.value = errorText(
        caught,
        "The company could not be added to tracking.",
      );
    } finally {
      finishRowAction(wishlistId, controller);
    }
  }

  function cancelLoad(invalidateCache = false): void {
    loadGeneration += 1;
    activeLoadController?.abort();
    activeLoadController = null;
    isRefreshing.value = false;
    const scope = cacheScope();
    if (invalidateCache) invalidateCompanyWishlistCache(scope);
  }

  function ownsAdd(
    controller: AbortController,
    requestSession: number,
    scope: string | null,
  ): boolean {
    return (
      addController === controller &&
      sessionGeneration === requestSession &&
      cacheScope() === scope &&
      !controller.signal.aborted
    );
  }

  function beginRowAction(
    wishlistId: string,
    action: "tracking" | "removing",
  ): {
    controller: AbortController;
    requestSession: number;
    scope: string | null;
  } | null {
    if (!wishlistId || pendingActions[wishlistId]) return null;
    cancelLoad(true);
    const controller = new AbortController();
    actionControllers.set(wishlistId, controller);
    pendingActions[wishlistId] = action;
    if (!activeAction.value) activeAction.value = wishlistId;
    return {
      controller,
      requestSession: sessionGeneration,
      scope: cacheScope(),
    };
  }

  function ownsRowAction(
    wishlistId: string,
    controller: AbortController,
    requestSession: number,
    scope: string | null,
  ): boolean {
    return (
      actionControllers.get(wishlistId) === controller &&
      sessionGeneration === requestSession &&
      cacheScope() === scope &&
      !controller.signal.aborted
    );
  }

  function nextActiveAction(): string {
    if (isAdding.value) return "add";
    return Object.keys(pendingActions)[0] ?? "";
  }

  function finishRowAction(
    wishlistId: string,
    controller: AbortController,
  ): void {
    if (actionControllers.get(wishlistId) !== controller) return;
    actionControllers.delete(wishlistId);
    delete pendingActions[wishlistId];
    if (activeAction.value === wishlistId)
      activeAction.value = nextActiveAction();
  }

  function dispose(): void {
    sessionGeneration += 1;
    cancelLoad();
    addController?.abort();
    addController = null;
    isAdding.value = false;
    for (const controller of actionControllers.values()) controller.abort();
    actionControllers.clear();
    for (const wishlistId of Object.keys(pendingActions))
      delete pendingActions[wishlistId];
    activeAction.value = "";
  }

  function reset(): void {
    const scope = cacheScope();
    dispose();
    invalidateCompanyWishlistCache(scope);
    phase.value = "loading";
    wishlist.value = [];
    sponsorCompanies.value = [];
    summary.value = emptySummary();
    generatedAt.value = "";
    policyVersion.value = "";
    companyInput.value = "";
    activeAction.value = "";
    errorMessage.value = "";
    validationMessage.value = "";
    actionNotice.value = "";
    Object.assign(filters, DEFAULT_WISHLIST_FILTERS);
    currentPage.value = 1;
  }

  watch(
    () => [filters.segment, filters.status, filters.industry, filters.query],
    () => {
      currentPage.value = 1;
    },
    { flush: "sync" },
  );

  return {
    actionNotice,
    activeAction,
    addCompany,
    companyCount,
    companyInput,
    currentPage,
    dispose,
    errorMessage,
    filteredCompanies,
    filters,
    formDisabledReason,
    generatedAt,
    industries,
    isAdding,
    isRefreshing,
    isUpdating,
    load,
    pageCount,
    pendingActions,
    phase,
    policyVersion,
    removeCompany,
    reset,
    setPage,
    sponsorCompanies,
    summary,
    trackCompany,
    validateCompany,
    validationMessage,
    visibleCompanies,
    wishlist,
  };
});
